import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { qualityAPI } from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import { usePortalMode } from '../../hooks/usePortalMode';
import { useAuth } from '../../contexts/AuthContext';

/**
 * CR-5887 (TEMPORARY — remove after testing).
 *
 * Helix-admin-only control to fire the Daily Quality Summary email on demand,
 * so QA doesn't have to wait for the 07:00 UTC Celery beat. Clicking the
 * button opens a dialog that asks who the email should be sent to (defaults
 * to the current user), then sends only to that address via
 * `POST /quality/daily-summary/send-now/?to=`, so it never spams other users.
 *
 * Renders nothing for non-Helix users. To remove the whole feature: delete
 * this file, its one usage in Quality.tsx, and `triggerDailyQualitySummary`
 * in `lib/api/quality.ts`.
 */
export function SendQualitySummaryTestButton() {
  const { isHelix } = usePortalMode();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email ?? '');
  const [sending, setSending] = useState(false);

  if (!isHelix) return null;

  const handleSend = async () => {
    const to = email.trim();
    if (!to) {
      toast.error('Enter an email address to send the test summary to.');
      return;
    }
    setSending(true);
    try {
      const { data } = await qualityAPI.triggerDailyQualitySummary(to);
      const sent = data.result?.sent ?? 0;
      if (sent > 0) {
        toast.success(`Daily Quality Summary sent to ${to}.`);
        setOpen(false);
      } else {
        toast.error(
          `No email sent to ${to} (${data.result?.reason ?? 'no matching recipient'}).`,
        );
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || 'Failed to send the test summary.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        title="Send the Daily Quality Summary email now (Helix-admin test trigger, CR-5887)"
      >
        <Mail className="h-4 w-4 mr-2" />
        Send test summary
      </Button>

      <Dialog open={open} onOpenChange={(next) => !sending && setOpen(next)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Daily Quality Summary</DialogTitle>
            <DialogDescription>
              Who should this test email be sent to? It goes only to this
              address — no other recipients are emailed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor="quality-summary-recipient">Send to</Label>
            <Input
              id="quality-summary-recipient"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="recipient@email.com"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending}>
              {sending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
